import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Info,
  ShieldCheck,
  LockKeyhole,
  HelpCircle,
  Settings,
  Sun,
  Moon,
  LogOut,
  ChevronDown,
  UserRound,
  Menu,
} from 'lucide-react';
import Portal from '@ui/components/atoms/Portal';
import Breadcrumbs from './Breadcrumbs';
import { getAuthenticatedUser } from '../data/settingsSelectors';
import { useAppState } from '../hooks/useAppState';
import { logoutApi, storageUrl } from '../services/api';

import serojaAppIcon from '../assets/images/seroja-appicon.png';

const ACCOUNT_ITEMS = [
  { key: 'about', label: 'Tentang', icon: Info, to: '/about' },
  { key: 'privacy', label: 'Privasi', icon: ShieldCheck, to: '/privacy' },
  { key: 'license', label: 'Lisensi', icon: LockKeyhole, to: '/license' },
  { key: 'help', label: 'Bantuan', icon: HelpCircle, to: '/help' },
];

export default function AppTopBar({ theme, onToggleTheme, onOpenMobileNav }) {
  const navigate = useNavigate();
  const { setCurrentUserUsername } = useAppState();
  const [userVersion, setUserVersion] = useState(0);
  const currentUser = getAuthenticatedUser();
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuCoords, setMenuCoords] = useState(null);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  // Re-read the API session after profile changes (name / avatar edits).
  useEffect(() => {
    const handler = () => setUserVersion((v) => v + 1);
    window.addEventListener('bursaku:user-updated', handler);
    return () => window.removeEventListener('bursaku:user-updated', handler);
  }, [userVersion]);

  const handleLogout = async () => {
    await logoutApi();
    setCurrentUserUsername('');
    navigate('/login');
  };

  const toggleMenu = () => {
    if (!menuOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuCoords({
        top: rect.bottom + 8,
        right: Math.max(8, window.innerWidth - rect.right),
      });
    }
    setMenuOpen((prev) => !prev);
  };

  // Portal click-outside — must check both trigger and popover refs.
  useEffect(() => {
    const handleClickOutside = (e) => {
      const isOutsideTrigger = triggerRef.current && !triggerRef.current.contains(e.target);
      const isOutsidePopover = popoverRef.current && !popoverRef.current.contains(e.target);
      if (isOutsideTrigger && isOutsidePopover) {
        setMenuOpen(false);
      }
    };

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  return (
    <header className="app-topbar">
      <div className="app-topbar__left">
        {onOpenMobileNav && (
          <button
            type="button"
            className="app-topbar__hamburger"
            onClick={onOpenMobileNav}
            aria-label="Buka menu"
            title="Menu"
          >
            <Menu size={20} />
          </button>
        )}
        <Breadcrumbs />
      </div>

      <div className="app-topbar__right">
        {/* ── Account menu ─────────────────────────────────────────── */}
        <div className="app-account">
          <button
            type="button"
            ref={triggerRef}
            className={`app-account__trigger${menuOpen ? ' app-account__trigger--open' : ''}`}
            onClick={toggleMenu}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
          >
            <span className="app-account__avatar">
              {currentUser.profile_picture ? (
                <img src={storageUrl(currentUser.profile_picture)} alt="" />
              ) : (
                <img src={serojaAppIcon} alt="" />
              )}
            </span>
            <span className="app-account__detail">
              <span className="app-account__name">{currentUser.name}</span>
              <span className="app-account__role">{currentUser.roleName}</span>
            </span>
            <ChevronDown size={16} className="app-account__chevron" />
          </button>

          {menuOpen && (
            <Portal>
              <div ref={popoverRef} className="app-account__menu" role="menu" style={menuCoords}>
                <div className="app-account__menu-header">
                  <span className="app-account__menu-avatar">
                    {currentUser.profile_picture ? (
                      <img src={storageUrl(currentUser.profile_picture)} alt="" />
                    ) : (
                      <UserRound size={18} />
                    )}
                  </span>
                  <div className="app-account__menu-user">
                    <span className="app-account__menu-name">{currentUser.name}</span>
                    <span className="app-account__menu-role">{currentUser.email}</span>
                  </div>
                </div>

                {ACCOUNT_ITEMS.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.key}
                      to={item.to}
                      className="app-account__menu-item"
                      role="menuitem"
                      onClick={() => setMenuOpen(false)}
                    >
                      <Icon size={16} />
                      <span>{item.label}</span>
                    </Link>
                  );
                })}

                <div className="app-account__menu-sep" />

                <Link to="/settings/general" className="app-account__menu-item" role="menuitem" onClick={() => setMenuOpen(false)}>
                  <Settings size={16} />
                  <span>Pengaturan</span>
                </Link>

                <button type="button" className="app-account__menu-item" role="menuitem" onClick={onToggleTheme}>
                  {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
                  <span>{theme === 'dark' ? 'Mode Terang' : 'Mode Gelap'}</span>
                </button>

                <div className="app-account__menu-sep" />

                <button type="button" className="app-account__menu-item app-account__menu-item--danger" role="menuitem" onClick={handleLogout}>
                  <LogOut size={16} />
                  <span>Logout</span>
                </button>
              </div>
            </Portal>
          )}
        </div>
      </div>
    </header>
  );
}
